import { createSelector } from 'redux-orm';

import orm from '../orm';
import { calculateCoefficient } from '../utils/calculateCoefficient';
import { selectPath } from './router';
import { selectCurrentUserId } from './users';
import { isLocalId } from '../utils/local-id';

export const makeSelectCardById = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return {
        ...cardModel.ref,
        coverUrl: cardModel.coverAttachment && cardModel.coverAttachment.coverUrl,
        isPersisted: !isLocalId(id),
      };
    },
  );

export const selectCardById = makeSelectCardById();

export const makeSelectUsersByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }
      return cardModel.users.toRefArray();
    },
  );

export const selectUsersByCardId = makeSelectUsersByCardId();

export const makeSelectLabelsByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.labels.toRefArray();
    },
  );

export const selectLabelsByCardId = makeSelectLabelsByCardId();






export const makeSelectTasksByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.getOrderedTasksQuerySet().toRefArray();
    },
  );





export const selectTasksByCardId = makeSelectTasksByCardId();

export const makeSelectLastActivityIdByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      const lastActivityModel = cardModel.getFilteredOrderedInCardActivitiesQuerySet().last();

      return lastActivityModel && lastActivityModel.id;
    },
  );

export const selectLastActivityIdByCardId = makeSelectLastActivityIdByCardId();

export const makeSelectNotificationsByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.getUnreadNotificationsQuerySet().toRefArray();
    },
  );

export const selectNotificationsByCardId = makeSelectNotificationsByCardId();

export const makeSelectNotificationsTotalByCardId = () =>
  createSelector(
    orm,
    (_, id) => id,
    ({ Card }, id) => {
      const cardModel = Card.withId(id);

      if (!cardModel) {
        return cardModel;
      }

      return cardModel.getUnreadNotificationsQuerySet().count();
    },
  );

export const selectNotificationsTotalByCardId = makeSelectNotificationsTotalByCardId();

export const selectCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);
    if (!cardModel) {
      return cardModel;
    }

    return cardModel.ref;
  },
);

export const selectUsersForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel.users.toRefArray();
  },
);

export const selectLabelsForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel.labels.toRefArray();
  },
);

export const selectTasksForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getOrderedTasksQuerySet()
      .toRefArray()
      .map((task) => ({
        ...task,
        isPersisted: !isLocalId(task.id),
      }));
  },
);

export const selectAttachmentsForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getOrderedAttachmentsQuerySet()
      .toRefArray()
      .map((attachment) => ({
        ...attachment,
        isCover: attachment.id === cardModel.coverAttachmentId,
        isPersisted: !isLocalId(attachment.id),
      }));
  },
);

export const selectActivitiesForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  (state) => selectCurrentUserId(state),
  ({ Card }, id, currentUserId) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getFilteredOrderedInCardActivitiesQuerySet()
      .toModelArray()
      .map((activityModel) => ({
        ...activityModel.ref,
        isPersisted: !isLocalId(activityModel.id),
        user: {
          ...activityModel.user.ref,
          isCurrent: activityModel.user.id === currentUserId,
        },
      }));
  },
);

export const selectNotificationIdsForCurrentCard = createSelector(
  orm,
  (state) => selectPath(state).cardId,
  ({ Card }, id) => {
    if (!id) {
      return id;
    }

    const cardModel = Card.withId(id);

    if (!cardModel) {
      return cardModel;
    }

    return cardModel
      .getUnreadNotificationsQuerySet()
      .toRefArray()
      .map((notification) => notification.id);
  },
);

export const makeSelectPointsDataByCardId = () =>
  createSelector(
    orm,
    (_, cardId) => cardId,
    (_, __, pointSystemId) => pointSystemId,
    (
      { CardPointData, CardPointAdvancedData, ContentCardDefault, ContentViewPlan, DampingCoefficient },
      cardId,
      pointSystemId,
    ) => {
      if (!cardId || !pointSystemId) {
        return null;
      }

      // Добавляем явную зависимость от DampingCoefficients для пересчета селектора
      // Используем toModelArray() для получения моделей, которые отслеживаются ORM
      const dampingCoefficientModels = DampingCoefficient.all()
        .filter({ pointSistemId: pointSystemId })
        .toModelArray();
      
      console.log(`[selectPointsDataByCardId] cardId: ${cardId}, pointSystemId: ${pointSystemId}, dampingCoefficientModels.length:`, dampingCoefficientModels.length);
      
      // Получаем хеш всех значений для явной зависимости от изменений
      // Включаем количество моделей в хеш, чтобы селектор пересчитывался при добавлении/удалении
      const dampingCoefficientsHash = dampingCoefficientModels.length > 0
        ? `${dampingCoefficientModels.length}:${dampingCoefficientModels
            .map(dc => `${dc.id}:${dc.from}:${dc.to}:${dc.value}`)
            .sort()
            .join('|')}`
        : '0';
      
      console.log(`[selectPointsDataByCardId] cardId: ${cardId}, dampingCoefficientsHash:`, dampingCoefficientsHash);
      
      // Используем хеш для явной зависимости - это заставляет селектор пересчитываться
      // при изменении количества или значений DampingCoefficients
      // Возвращаем хеш как часть результата селектора, чтобы он был частью зависимостей
      const _dampingCoefficientsHash = dampingCoefficientsHash;

      const cardPointDataModel = CardPointData.all()
        .filter({ cardId, pointSistemId: pointSystemId })
        .first();

      const cardPointAdvancedDataModels = CardPointAdvancedData.all()
        .filter({ cardId, pointSistemId: pointSystemId })
        .toRefArray();

      if (!cardPointDataModel && cardPointAdvancedDataModels.length === 0) {
        return null;
      }

      const cardPointData = cardPointDataModel?.ref || null;
      const cardPointAdvancedData = cardPointAdvancedDataModels || [];
      
      // Получаем ContentCardDefault через ccdId (явно), чтобы:
      // - корректно учитывать базовые баллы из CardPointData
      // - создать явную зависимость селектора от ContentCardDefault (реактивный пересчёт)
      let contentCardDefault = null;
      if (cardPointDataModel?.ccdId) {
        const contentCardDefaultModel = ContentCardDefault.withId(cardPointDataModel.ccdId);
        if (
          contentCardDefaultModel &&
          contentCardDefaultModel.pointSistemId === pointSystemId
        ) {
          contentCardDefault = contentCardDefaultModel.ref;
        }
      }

      // Получаем текущий месяц и год
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // getMonth() возвращает 0-11, нужно 1-12

      // Получаем ContentViewPlan для текущего месяца/года
      // Используем toModelArray() для отслеживания изменений
      const contentViewPlanModels = ContentViewPlan.all()
        .filter({
          pointSistemId: pointSystemId,
          year: currentYear,
          month: currentMonth,
        })
        .toModelArray();
      const contentViewPlanModel = contentViewPlanModels.length > 0 ? contentViewPlanModels[0] : null;
      const contentViewPlan = contentViewPlanModel?.ref || null;
      
      // Добавляем зависимость от ContentViewPlan для пересчета селектора
      const contentViewPlanHash = contentViewPlanModel
        ? `${contentViewPlanModel.id}:${contentViewPlanModel.year}:${contentViewPlanModel.month}:${contentViewPlanModel.photo}:${contentViewPlanModel.video}:${contentViewPlanModel.caseCount}`
        : '0';

      // Получаем DampingCoefficients для данной системы баллов
      // Используем уже полученные модели из dampingCoefficientModels
      const dampingCoefficients = dampingCoefficientModels
        .sort((a, b) => (a.id || 0) - (b.id || 0))
        .map(model => model.ref);

      // Рассчитываем коэффициент
      let coefficient = 1;
      if (contentCardDefault && cardPointData && dampingCoefficients.length > 0) {
        coefficient = calculateCoefficient({
          contentCardDefault,
          cardPointData,
          contentViewPlan,
          dampingCoefficients,
        });
      }

      // Считаем сумму базовых и дополнительных баллов
      let basePoints = 0;
      if (contentCardDefault && contentCardDefault.value) {
        basePoints = contentCardDefault.value;
      }
      let extraPoints = 0;
      cardPointAdvancedData.forEach((item) => {
        if (item.value) {
          extraPoints += item.value;
        }
      });

      // Применяем коэффициент: totalPoints = (basePoints + extraPoints) * coefficient
      const totalPoints = (basePoints + extraPoints) * coefficient;

      console.log(`[selectPointsDataByCardId] cardId: ${cardId}, basePoints: ${basePoints}, extraPoints: ${extraPoints}, coefficient: ${coefficient}, totalPoints: ${totalPoints}`);

      return {
        contentCardDefault,
        cardPointAdvancedData,
        totalPoints,
        coefficient,
        basePoints,
        extraPoints,
        // Добавляем хеш DampingCoefficients и ContentViewPlan в результат, чтобы селектор пересчитывался
        // при изменении DampingCoefficients или ContentViewPlan (даже если другие данные не изменились)
        _dampingCoefficientsHash,
        _contentViewPlanHash: contentViewPlanHash,
      };
    },
  );

export const selectPointsDataByCardId = makeSelectPointsDataByCardId();

export default {
  makeSelectCardById,
  selectCardById,
  makeSelectUsersByCardId,
  selectUsersByCardId,
  makeSelectLabelsByCardId,
  selectLabelsByCardId,
  makeSelectTasksByCardId,
  selectTasksByCardId,
  makeSelectLastActivityIdByCardId,
  selectLastActivityIdByCardId,
  makeSelectNotificationsByCardId,
  selectNotificationsByCardId,
  makeSelectNotificationsTotalByCardId,
  selectNotificationsTotalByCardId,
  selectCurrentCard,
  selectUsersForCurrentCard,
  selectLabelsForCurrentCard,
  selectTasksForCurrentCard,
  selectAttachmentsForCurrentCard,
  selectActivitiesForCurrentCard,
  selectNotificationIdsForCurrentCard,
  makeSelectPointsDataByCardId,
  selectPointsDataByCardId,
};
